import React, { useEffect, useState } from 'react';
import { Form, Input, Select, Checkbox } from 'antd';
import GenderSelect from 'components/form/GenderSelect';
import BirthdayInput from 'components/form/BirthdayInput';
import CredPhoneInput from '../../components/CredPhoneInput';
import StateSelect from 'components/form/StateSelect';
import AutoAddressInput from 'components/AutoAddressInput';
import useSettings from 'hooks/useSettings';
import { lookupNpiStandalone } from 'api/common';
import NpiRegistryBox from 'pages/InsuranceBillingOnboarding/components/NpiRegistryBox';
import { EnumFields } from 'types/enumerationData';
import SectionHeader from '../../components/SectionHeader';
import OptLabel from '../../components/OptLabel';
import FormValidationHelper from 'utils/validation';
import { CITIZENSHIP, selectOptions } from '../../constants';
import s from '../../s.module.less';

// 只留数字、最多 10 位：与 insurance billing 的 NPI 输入口径一致，避免带空格/连字符触发无谓查询
const normalizeNpi = (v?: string) => (v || '').replace(/\D/g, '').slice(0, 10);

export default function StepIdentity() {
    const form = Form.useFormInstance();
    const [setting] = useSettings();
    const stateOption = setting[EnumFields.STATE] || [];
    const credentialOptions = setting[EnumFields.CREDENTIAL_TYPE] || [];

    // “Different mailing address” 为纯 UI 开关（接口以 mailingAddress 是否非空表达），
    // 用本地 state 控制，不进表单；回填已保存的 mailingAddress 时自动展开。
    // 注意 antd 5.2.3 的 useWatch 只见已注册字段：收起时 mailing 字段未注册，setFieldsValue 灌进
    // store 的值 useWatch 看不到，须直读 store（getFieldValue），否则已保存邮寄地址永远不展开。
    const mailingAddress = Form.useWatch('mailingAddress', form) ?? form.getFieldValue('mailingAddress');
    const [showMailing, setShowMailing] = useState(false);
    useEffect(() => {
        if (
            mailingAddress?.practiceAddress ||
            mailingAddress?.practiceCity ||
            mailingAddress?.practiceState ||
            mailingAddress?.practiceZip
        )
            setShowMailing(true);
    }, [mailingAddress]);

    return (
        <>
            <div className={s.wizSection}>
                <SectionHeader title="Contact information" />
                <div className={s.wizRow}>
                    <Form.Item
                        name="email"
                        label="Primary work email"
                        rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}
                    >
                        <Input size="large" placeholder="you@example.com" disabled />
                    </Form.Item>
                    <Form.Item
                        name="tel"
                        label="Mobile phone"
                        rules={[{ required: true, message: 'Enter your phone number' }]}
                    >
                        <CredPhoneInput size="large" placeholder="(555) 555-5555" />
                    </Form.Item>
                </div>
                {/* V2：eFax 推销从 Basic information 移到 submitted 页（Fax 留空才显示），此处只留纯输入 */}
                <div className={s.wizRow}>
                    <Form.Item name="fax" label="Fax">
                        <CredPhoneInput size="large" placeholder="(555) 555-5555" />
                    </Form.Item>
                </div>
                <Form.Item
                    name={['contactAddress', 'practiceAddress']}
                    label="Address"
                    rules={[{ required: true, message: 'Enter your address' }]}
                >
                    <AutoAddressInput
                        size="large"
                        placeholder="Street address"
                        addCityToAddress
                        onStateChange={(val) => {
                            const target = stateOption.find((item) => item.value === val || item.label === val);
                            form.setFieldValue(['contactAddress', 'practiceState'], target ? target.value : val);
                        }}
                        onCityChange={(val) => form.setFieldValue(['contactAddress', 'practiceCity'], val)}
                        onPostcodeChange={(val) => form.setFieldValue(['contactAddress', 'practiceZip'], val)}
                    />
                </Form.Item>
                <div className={s.wizRow}>
                    <Form.Item
                        name={['contactAddress', 'practiceCity']}
                        label="City"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Input size="large" placeholder="City" />
                    </Form.Item>
                    <Form.Item
                        name={['contactAddress', 'practiceState']}
                        label="State"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <StateSelect size="large" placeholder="State" />
                    </Form.Item>
                    <Form.Item
                        name={['contactAddress', 'practiceZip']}
                        label="ZIP code"
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Input size="large" placeholder="ZIP code" />
                    </Form.Item>
                </div>
                <Form.Item>
                    <Checkbox
                        checked={showMailing}
                        onChange={(e) => {
                            setShowMailing(e.target.checked);
                            // 取消勾选时清空 mailingAddress，接口据此判定“无不同的邮寄地址”
                            if (!e.target.checked) form.setFieldValue('mailingAddress', undefined);
                        }}
                    >
                        Different mailing address
                    </Checkbox>
                </Form.Item>
                {showMailing && (
                    <>
                        <Form.Item
                            name={['mailingAddress', 'practiceAddress']}
                            label="Mailing address"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <Input size="large" placeholder="Street address" />
                        </Form.Item>
                        <div className={s.wizRow}>
                            <Form.Item
                                name={['mailingAddress', 'practiceCity']}
                                label="City"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Input size="large" placeholder="City" />
                            </Form.Item>
                            <Form.Item
                                name={['mailingAddress', 'practiceState']}
                                label="State"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <StateSelect size="large" placeholder="State" />
                            </Form.Item>
                            <Form.Item
                                name={['mailingAddress', 'practiceZip']}
                                label="ZIP code"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Input size="large" placeholder="ZIP code" />
                            </Form.Item>
                        </div>
                    </>
                )}
            </div>

            <div className={s.wizSection}>
                <SectionHeader title="Personal information" />
                <div className={s.wizRow}>
                    <Form.Item name="firstName" label="First name" rules={[{ required: true, message: 'Required' }]}>
                        <Input size="large" placeholder="Legal first name" />
                    </Form.Item>
                    <Form.Item name="middleName" label={<OptLabel>Middle name</OptLabel>}>
                        <Input size="large" placeholder="Middle name" />
                    </Form.Item>
                    <Form.Item name="lastName" label="Last name" rules={[{ required: true, message: 'Required' }]}>
                        <Input size="large" placeholder="Legal last name" />
                    </Form.Item>
                </div>
                <Form.Item name="formerName" label={<OptLabel>Former name or alias</OptLabel>}>
                    <Input size="large" placeholder="Maiden name or other names used" />
                </Form.Item>
                <div className={s.wizRow}>
                    <Form.Item
                        name="birthDate"
                        label="Date of birth"
                        rules={[
                            { required: true, message: 'Required' },
                            FormValidationHelper.validateBirthday('Enter a valid date of birth'),
                        ]}
                    >
                        <BirthdayInput size="large" placeholder="MM/DD/YYYY" />
                    </Form.Item>
                    <Form.Item name="gender" label="Gender" rules={[{ required: true, message: 'Required' }]}>
                        <GenderSelect size="large" placeholder="Select" />
                    </Form.Item>
                </div>
                <div className={s.wizRow}>
                    {/* 位数/查询结果一律由下方的 NpiRegistryBox 出（与 insurance billing 一致），
                        这里只留必填，否则同一条错误会重复显示两遍 */}
                    <Form.Item
                        name="npi"
                        label="Individual NPI (Type 1)"
                        normalize={normalizeNpi}
                        rules={[{ required: true, message: 'Required' }]}
                    >
                        <Input size="large" inputMode="numeric" maxLength={10} placeholder="10 digit NPI" />
                    </Form.Item>
                    <Form.Item name="credential" label="Credentials" rules={[{ required: true, message: 'Required' }]}>
                        <Select
                            size="large"
                            mode="multiple"
                            showSearch
                            placeholder="Select"
                            options={credentialOptions}
                        />
                    </Form.Item>
                </div>
                {/* NPI Registry 校验：必须是个人 NPI（Type 1），且登记姓名要与上面填的姓名一致。
                    查询走 api/common 的独立端点——credentialing 用户不一定开通了 Insurance Billing，
                    onboarding 那条会校验 owner + api key。credentialing 不收 taxonomy，关掉下拉 */}
                <NpiRegistryBox
                    ns={[]}
                    expectType="Type 1"
                    nameFields={{ firstName: ['firstName'], lastName: ['lastName'] }}
                    showTaxonomy={false}
                    className={s.npiBox}
                    lookup={lookupNpiStandalone}
                />
                <Form.Item
                    name="citizenship"
                    label="Citizenship or nationality"
                    initialValue="US citizen"
                    rules={[{ required: true, message: 'Required' }]}
                >
                    <Select size="large" placeholder="Select" options={selectOptions(CITIZENSHIP)} />
                </Form.Item>
                <Form.Item noStyle shouldUpdate={(p, c) => p.citizenship !== c.citizenship}>
                    {({ getFieldValue }) =>
                        getFieldValue('citizenship') && getFieldValue('citizenship') !== 'US citizen' ? (
                            <Form.Item name="workAuthorizationType" label="Work authorization type">
                                <Input size="large" placeholder="e.g. H-1B, J-1, EAD, OPT" />
                            </Form.Item>
                        ) : null
                    }
                </Form.Item>
            </div>
        </>
    );
}
